/**
 * Pagos de clientes sobre facturas emitidas: abonos con fecha, monto y medio de pago. El saldo
 * por cobrar descuenta las notas de crédito emitidas y los pagos vigentes; con saldo $0 la
 * factura queda pagada. Un pago no se borra: se anula con motivo.
 */
import { NotaCreditoFactura } from "@/helpers/notasCredito";

export const MEDIOS_PAGO = ["Transferencia", "Depósito", "Cheque", "Efectivo", "Tarjeta", "Otro"] as const;
export type MedioPago = typeof MEDIOS_PAGO[number];

export interface PagoFactura {
  id?: string;
  idFactura: string;
  folioFactura: number | null;
  codigoContrato: number | string;
  periodoFacturado: string;
  clienteSnapshot: { nombre: string; rut: string; [k: string]: unknown };
  fecha: string; // YYYY-MM-DD
  monto: number;
  medio: MedioPago;
  banco: string;
  numeroOperacion: string;
  observacion: string;
  estado: "Vigente" | "Anulado";
  motivoAnulacion?: string;
  creadoPor?: string;
  creadoEn?: string;
  anuladoPor?: string;
  anuladoEn?: string;
}

interface FacturaBase {
  id?: string;
  folio?: number | null;
  codigoContrato?: number | string;
  periodoFacturado: string;
  clienteSnapshot: { nombre: string; rut: string; [k: string]: unknown };
  montoTotal: number;
}

export function pagosDeFactura(pagos: PagoFactura[], idFactura?: string): PagoFactura[] {
  return pagos.filter((p) => p.idFactura === idFactura && p.estado === "Vigente");
}

export function totalPagado(pagos: PagoFactura[], idFactura?: string): number {
  return pagosDeFactura(pagos, idFactura).reduce((s, p) => s + (Number(p.monto) || 0), 0);
}

/** Notas de crédito emitidas de la factura (las en borrador aún no rebajan la deuda). */
export function totalNotasEmitidas(notas: NotaCreditoFactura[], idFactura?: string): number {
  return notas
    .filter((n) => n.idFactura === idFactura && n.estado === "Emitida")
    .reduce((s, n) => s + (Number(n.montoTotal) || 0), 0);
}

/** Lo que falta cobrar: total − notas de crédito emitidas − pagos vigentes (puede ser negativo: saldo a favor). */
export function saldoPorCobrar(factura: FacturaBase, notas: NotaCreditoFactura[], pagos: PagoFactura[]): number {
  return Math.round(factura.montoTotal - totalNotasEmitidas(notas, factura.id) - totalPagado(pagos, factura.id));
}

export interface DatosPago {
  fecha: string;
  monto: number;
  medio: MedioPago | "";
  banco: string;
  numeroOperacion: string;
  observacion: string;
}

/** Primer problema del pago, o "" si se puede registrar. */
export function validarPago(datos: DatosPago, saldo: number, hoy = new Date()): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datos.fecha || "")) return "Indica la fecha del pago";
  const [y, m, d] = datos.fecha.split("-").map(Number);
  const fecha = new Date(y, m - 1, d);
  if (fecha.getTime() > new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()).getTime()) return "La fecha del pago no puede ser futura";
  const monto = Math.round(Number(datos.monto) || 0);
  if (monto <= 0) return "El monto debe ser mayor a 0";
  if (monto > saldo) return `El monto supera el saldo por cobrar (${saldo})`;
  if (!datos.medio) return "Elige el medio de pago";
  if (["Transferencia", "Depósito", "Cheque"].includes(datos.medio) && !String(datos.numeroOperacion || "").trim())
    return datos.medio === "Cheque" ? "Indica el número del cheque" : "Indica el número de operación";
  return "";
}

/** Pago listo para guardar (sin id ni datos de auditoría). */
export function armarPago(factura: FacturaBase, datos: DatosPago): Omit<PagoFactura, "id" | "creadoPor" | "creadoEn"> {
  return {
    idFactura: factura.id || "",
    folioFactura: factura.folio ?? null,
    codigoContrato: factura.codigoContrato ?? "",
    periodoFacturado: factura.periodoFacturado,
    clienteSnapshot: factura.clienteSnapshot,
    fecha: datos.fecha,
    monto: Math.round(Number(datos.monto) || 0),
    medio: datos.medio as MedioPago,
    banco: String(datos.banco || "").trim(),
    numeroOperacion: String(datos.numeroOperacion || "").trim(),
    observacion: String(datos.observacion || "").trim(),
    estado: "Vigente",
  };
}

/**
 * Estado de cobro que corresponde: con saldo ≤ 0 y algún pago, Pagada; si estaba Pagada y
 * vuelve a quedar saldo (se anuló un pago), Emitida. Las anuladas no se tocan.
 */
export function estadoSegunCobro(
  estado: string,
  saldo: number,
  hayPagos: boolean
): "Emitida" | "Pagada" | null {
  if (estado === "Emitida" && saldo <= 0 && hayPagos) return "Pagada";
  if (estado === "Pagada" && saldo > 0) return "Emitida";
  return null;
}
